import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Loading } from '@components/loading';
import { LinkStatus } from '@components/link-status';
import { Header } from '@components/questionnaire/header';
import { LangSwitcher } from '@components/form/lang-switcher';
import { Button } from '@components/form/button';
import { DynamicTypography } from '@components/DynamicTypography';
import { QuestionnaireSelectorState } from '@app/types';
import { requestLink, setLanguage, setStatus } from '@store/sagas/questionnaire';
import { I18 } from '@components/form/i18';

type RouteParams = {
  token: string;
  promId: string;
};

export const Consent = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, promId } = useParams<RouteParams>();

  const { error, status, loading, language, patient } = useSelector(
    (state: QuestionnaireSelectorState) => ({
      error: state.app.error,
      status: state.app.status,
      loading: state.app.loading,
      language: state.app.language,
      patient: state.app.patient
    })
  );

  useEffect(() => {
    dispatch(requestLink({ token, promId }));
  }, [dispatch, token, promId]);

  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [i18n, language]);

  const handleContinue = () => {
    navigate(`/questionnaire/${token}/start`, { replace: true });
  };

  const handleSetStatus = (newStatus: string) => {
    dispatch(setStatus(newStatus));
  };

  const handleSetLanguage = (language: string) => {
    dispatch(setLanguage(language));
  };

  if (loading) return <Loading />;
  if (error) return <LinkStatus type="error" />;
  if (status === 'denied') return <LinkStatus type="denied" />;

  return (
    <div className="container">
      <Header
        title={t('common:welcomePage.title')}
        className="consent-header"
        pii={{ firstName: patient?.firstName ?? '', lastName: patient?.lastName ?? '' }}
      />
      <div className="consent-container">
        <LangSwitcher active={i18n.language} onChange={handleSetLanguage} />

        <div className="consent-welcome">
          <DynamicTypography content={t('common:welcomePage.p1')} />
          <DynamicTypography content={t('common:welcomePage.p2')} />
          <DynamicTypography content={t('common:welcomePage.p3')} />
        </div>

        <div className="consent-actions">
          <p>{t('common:welcomePage.consent')}</p>

          <div className="consent-buttons">
            <Button
              title={t('common:welcomePage.no')}
              type="secondary"
              onClick={() => handleSetStatus('denied')}
            />
            <Button
              title={t('common:welcomePage.yes')}
              type={status === 'yes' ? 'primary' : 'secondary'}
              onClick={() => handleSetStatus('yes')}
            />
          </div>

          {status === 'yes' && (
            <div className="survey-continue">
              <p>
                <I18 text="common:welcomePage.hint" />
              </p>
              <Button
                title={t('common:welcomePage.continue')}
                type="primary"
                onClick={handleContinue}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
